import { useEffect, useRef, useState } from 'react';
import travelVideo from '../assets/waybook_travel.mp4';

type HeroVisualProps = {
  showVideo: boolean;
  progress?: number; // 0..1 controls green + reveal before unlock
};

export default function HeroVisual({ showVideo, progress = 0 }: HeroVisualProps) {
  const pathRef = useRef<SVGPathElement | null>(null);
  const [pathLen, setPathLen] = useState(0);

  useEffect(() => {
    if (pathRef.current) {
      const len = pathRef.current.getTotalLength();
      setPathLen(len);
    }
  }, []);

  // Normalize progress: accept 0..1 or 0..10
  const p = (() => {
    const raw = Number.isFinite(progress as number) ? (progress as number) : 0;
    if (raw <= 1) return Math.max(0, Math.min(raw, 1));
    return Math.max(0, Math.min(raw / 10, 1));
  })();

  return (
    <div className="absolute inset-0 z-0 pointer-events-none">
      {/* Bottom layer: video (above SVG to cover it via mask) */}
      <div
        className={"absolute inset-0 transition-opacity duration-300 ease-out z-10"}
        style={{ opacity: showVideo || p === 1 ? 1 : 0 }}
      >
        {/* Reveal video with bubble mask effect */}
        <div className="absolute inset-0">
          <div className="relative h-full w-full">
            <video
              className="h-full w-full object-cover"
              src={travelVideo}
              autoPlay
              muted
              loop
              playsInline
              style={{ objectPosition: 'center center' }}
            />
            {/* Dark overlay */}
            <div className="absolute inset-0 bg-black/50" />
          </div>
        </div>
      </div>

      {/* Top layer: animated SVG route */}
      <div
        className={`absolute inset-0 transition-opacity duration-700 ease-[cubic-bezier(0.22,0.61,0.36,1)] ${
          showVideo ? 'opacity-0' : 'opacity-100'
        }`}
        style={{ opacity: showVideo ? 0 : p < 1 ? 1 : 0 }}
      >
        {/* Inline SVG with wave filter and animated route */}
        <svg className="w-full h-auto max-h-[900px]" viewBox="0 0 2638 900" preserveAspectRatio="xMidYMin meet" xmlns="http://www.w3.org/2000/svg">
          <defs>
          </defs>

          {/* Apply bubble mask during transition */}
          <g>
            {/* Subtle static route for context */}
            <path
              d="M77.8798 -151.5C601.38 -263.5 406.379 722.5 131.879 684.5C-142.621 646.5 109.55 159.244 565.879 506C1176.5 970 2576.63 1080.5 2117.38 375C1689.38 -282.5 1325.88 899 2273.88 487C3032.28 157.4 2259.67 -756.5 2166 -1005.5"
              stroke="#A7E826"
              strokeOpacity={0.25}
              strokeWidth={9}
              fill="none"
              strokeDasharray="18 18"
            />

            {/* Dotted moving path like CodePen */}
            <path
              ref={pathRef}
              d="M77.8798 -151.5C601.38 -263.5 406.379 722.5 131.879 684.5C-142.621 646.5 109.55 159.244 565.879 506C1176.5 970 2576.63 1080.5 2117.38 375C1689.38 -282.5 1325.88 899 2273.88 487C3032.28 157.4 2259.67 -756.5 2166 -1005.5"
              stroke="#A7E826"
              strokeWidth={9}
              strokeLinecap="round"
              fill="none"
              style={{
                ['--dot-gap' as any]: 18,
                strokeDasharray: '0 18',
              }}
              className="route-dotted-animate"
            />
          </g>
        </svg>
      </div>
    </div>
  );
}
